/**
 * Per-user agent eligibility — the ONE helper every entry decision point
 * uses (FR-ACCESS-1/3, FR-SHARED-5): the home last-agent redirect, the /app
 * chooser, the intro tour, and the console guard. Generic over agents (not
 * Vera-specific) so Maximus reuses it unchanged.
 *
 * Eligibility = the registry says the agent is `available` (stage-scoped at
 * build time, e.g. Vera only in beta builds) AND, when the agent declares
 * `requiredGroups`, the signed-in user holds at least one of them (ANY-of,
 * from the access token's `cognito:groups` claim).
 *
 * UX-gating only: the agents runtime enforces the same groups
 * authoritatively and 403s ineligible invocations regardless of what the
 * browser shows.
 */

import { AGENTS, AGENT_LIST, isAgentSlug, type Agent, type AgentSlug } from './registry.ts';
import type { AuthUser } from '../auth/AuthContext.ts';

/** True when `slug` names an agent this user can enter right now. */
export function isAgentEnabledFor(slug: string, user: AuthUser | null): slug is AgentSlug {
  if (!isAgentSlug(slug)) {
    return false;
  }
  const agent = AGENTS[slug];
  if (!agent.available) {
    return false;
  }
  const required = agent.requiredGroups;
  if (required === undefined || required.length === 0) {
    return true;
  }
  const groups = user?.groups ?? [];
  return groups.some((group) => required.includes(group));
}

/** The agents this user can enter, in registry order. Drives the chooser's
 *  sole-agent auto-continue and any per-user agent listing. */
export function agentsEnabledFor(user: AuthUser | null): readonly Agent[] {
  return AGENT_LIST.filter((agent) => isAgentEnabledFor(agent.slug, user));
}
